import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCertificate } from '../contexts/CertificateContext';
import { useCourse } from '../contexts/CourseContext';
import {
  Award,
  Search,
  Trophy,
  Star,
  TrendingUp,
  Download,
  Share2,
  Eye,
  RefreshCw,
  BookOpen,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

const Certificates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    loading,
    getUserCertificates,
    downloadCertificate,
    shareCertificate,
    getCertificateStats,
    loadMyCertificates,
    issueCertificate,
  } = useCertificate();
  const { enrolledCourses } = useCourse();

  const [searchQuery, setSearchQuery] = useState('');
  const [claiming, setClaiming] = useState({});

  const userCertificates = getUserCertificates();
  const stats = getCertificateStats();

  // Cours inscrits sans certificat émis → récupération possible
  const certifiedCourseIds = new Set(userCertificates.map(c => String(c.course_id || c.course?.id)));
  const enrolledWithoutCert = enrolledCourses.filter(
    c => c.id && !certifiedCourseIds.has(String(c.id))
  );

  // Filtrer les certificats
  const filteredCertificates = userCertificates.filter(cert => {
    const title = cert.course?.title || cert.metadata?.course_title || '';
    return searchQuery === '' || title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleDownload = async (cert) => {
    toast.loading('Ouverture du certificat pour impression/PDF…', { id: 'dl' });
    const result = await downloadCertificate(cert.id);
    if (result.success) {
      toast.success('Certificat ouvert !', { id: 'dl' });
    } else {
      toast.error(result.error || 'Erreur', { id: 'dl' });
    }
  };

  const handleShare = async (cert) => {
    const result = await shareCertificate(cert.id, 'linkedin');
    if (result.success) {
      window.open(result.shareLink, '_blank');
      toast.success('Partagé sur LinkedIn !');
    } else {
      toast.error(result.error || 'Erreur de partage');
    }
  };

  const handleClaimCertificate = async (courseId, courseTitle) => {
    setClaiming(prev => ({ ...prev, [courseId]: true }));
    const toastId = toast.loading(`Vérification de l'éligibilité pour "${courseTitle}"…`);
    const result = await issueCertificate(courseId);
    setClaiming(prev => ({ ...prev, [courseId]: false }));
    if (result?.certificate) {
      toast.success('Certificat émis avec succès !', { id: toastId });
      await loadMyCertificates();
    } else if (result?.alreadyIssued) {
      toast.success('Certificat déjà enregistré — liste actualisée.', { id: toastId });
      await loadMyCertificates();
    } else if (result?.paiementRequis && result?.lienAchat?.startsWith('https://app.innovactive360.cloud/')) {
      // Cours réussi, certificat à régler : achat sur la plateforme, puis retour ici pour l'obtenir
      toast.dismiss(toastId);
      if (window.confirm(`${result.reason}\n\nAller à la page de paiement ?`)) window.location.assign(result.lienAchat);
    } else {
      toast.error(result?.reason || result?.error || 'Conditions non remplies pour l\'instant.', { id: toastId, duration: 8000 });
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  if (!user) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Accès non autorisé</h1>
          <p className="text-gray-600 dark:text-gray-400">Vous devez être connecté pour voir vos certificats.</p>
        </div>
      </div>
    );
  }

  if (loading && userCertificates.length === 0) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement de vos certificats…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mes Certificats</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Consultez et partagez vos certifications
              </p>
            </div>
          </div>
          <button
            onClick={loadMyCertificates}
            className="p-2 text-gray-500 hover:text-primary transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCertificates}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Émis</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.issuedCertificates}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Score moyen</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.averageScore}%</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Ce mois</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {userCertificates.filter(c => {
                    if (!c.issued_at) return false;
                    const d = new Date(c.issued_at);
                    const now = new Date();
                    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un certificat par titre de cours…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Certificats manquants — récupération */}
        {enrolledWithoutCert.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 text-sm">
                  Certificats potentiellement manquants
                </h3>
                <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-0.5">
                  Vous êtes inscrit à ces cours sans certificat enregistré. Si vous avez validé toutes les évaluations, cliquez sur "Réclamer".
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {enrolledWithoutCert.map(course => (
                <div key={course.id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg px-4 py-2.5 border border-yellow-100 dark:border-yellow-800">
                  <div className="flex items-center gap-2 min-w-0">
                    <BookOpen className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                    <span className="text-sm text-gray-900 dark:text-white truncate">{course.title}</span>
                    {course.progress != null && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{Math.round(course.progress)}%</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleClaimCertificate(course.id, course.title)}
                    disabled={claiming[course.id]}
                    className="ml-3 flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-60 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    <Award className="w-3.5 h-3.5" />
                    {claiming[course.id] ? 'Vérification…' : 'Réclamer'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liste des certificats */}
        {filteredCertificates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredCertificates.map(cert => {
              const title = cert.course?.title || cert.metadata?.course_title || 'Cours';
              const score = Math.round(Number(cert.score || 0));
              return (
                <div
                  key={cert.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Top colour bar */}
                  <div className="h-2 bg-gradient-to-r from-primary to-primary/60" />
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Award className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate text-lg">{title}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Émis le {formatDate(cert.issued_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        Score : <span className="font-medium text-gray-900 dark:text-white ml-1">{score}%</span>
                      </div>
                      {cert.metadata?.modules_validated && (
                        <div className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {cert.metadata.modules_validated} module{cert.metadata.modules_validated > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={() => navigate(`/certificates/${cert.id}`)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm"
                      >
                        <Eye className="w-4 h-4" /> Voir
                      </button>
                      <button
                        onClick={() => handleDownload(cert)}
                        className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                        title="Imprimer / PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShare(cert)}
                        className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                        title="Partager sur LinkedIn"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {searchQuery ? 'Aucun certificat trouvé' : 'Aucun certificat encore'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchQuery
                ? 'Essayez de modifier votre recherche.'
                : 'Complétez un cours et validez toutes ses évaluations pour obtenir votre premier certificat.'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/courses')}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90"
              >
                Découvrir les cours
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificates;
